import React from "react";
import { useTranslation } from "react-i18next";
import { DealerJackPlayer } from "./DealerJackProtocol";
import { BlackjackGamePhase } from "./DealerJackReducer";

interface DealerJackPlayerListProps {
  players: DealerJackPlayer[];
  myId: number;
  showCards?: boolean;
  showBets?: boolean;
  gamePhase?: string; // 게임 페이즈 추가
  isBettingPhase?: boolean; // 베팅 단계 여부
  betsRevealed?: boolean; // 베팅 공개 여부
  actionsRevealed?: boolean; // 액션 결과 공개 여부 (Private Results용)
}

const DealerJackPlayerList: React.FC<DealerJackPlayerListProps> = ({
  players,
  myId,
  showCards = true,
  showBets = true,
  gamePhase,
  isBettingPhase = false,
  betsRevealed = true,
  actionsRevealed = true,
}) => {
  const { t } = useTranslation();

  // Removed unused status icon function - status is now shown inline

  // Suit helper functions for card display
  const getSuitIcon = (suit: string) => {
    switch (suit) {
      case "hearts":
        return "♥";
      case "diamonds":
        return "♦";
      case "clubs":
        return "♣";
      case "spades":
        return "♠";
      default:
        return "";
    }
  };

  const getSuitColor = (suit: string) => {
    return suit === "hearts" || suit === "diamonds"
      ? "text-red-500"
      : "text-black";
  };

  // Player cards rendering with actual card data
  const renderPlayerCards = (player: DealerJackPlayer) => {
    if (!showCards || player.cards.length === 0) return null;

    return (
      <div className="mt-2">
        {/* Cards and total in same row */}
        <div className="flex items-center gap-2">
          {/* Display cards */}
          <div className="flex flex-wrap gap-0.5">
            {player.cards.map((card, cardIndex) => (
              <div
                key={cardIndex}
                className="w-6 h-8 bg-white rounded border border-gray-300 flex flex-col items-center justify-center text-xs shadow-sm"
              >
                <span
                  className={`${getSuitColor(card.suit)} text-xs leading-none`}
                >
                  {getSuitIcon(card.suit)}
                </span>
                <span
                  className={`${getSuitColor(card.suit)} font-bold text-xs`}
                >
                  {card.rank}
                </span>
              </div>
            ))}
          </div>

          {/* Hand value and status in same row */}
          <div className="flex items-center text-white text-sm gap-2">
            <span className="font-bold">{player.handValue}</span>
            {player.isBlackjack && (
              <span className="text-yellow-400 font-bold text-xs">BJ!</span>
            )}
            {player.isBust && (
              <span className="text-red-400 font-bold text-xs">BUST!</span>
            )}
            {player.isEliminated && (
              <span className="text-red-400 font-bold text-xs">파산</span>
            )}
            {player.isSurrendered && !player.isEliminated && (
              <span className="text-orange-400 font-bold text-xs">항복</span>
            )}
            {player.isDoubledDown &&
              !player.isBust &&
              !player.isBlackjack &&
              !player.isEliminated && (
                <span className="text-purple-400 font-bold text-xs">2X</span>
              )}
            {player.isStanding &&
              !player.isBust &&
              !player.isBlackjack &&
              !player.isDoubledDown &&
              !player.isSurrendered &&
              !player.isEliminated && (
                <span className="text-green-400 text-xs">Stand</span>
              )}
          </div>
        </div>
      </div>
    );
  };

  const renderPlayerCard = (player: DealerJackPlayer) => {
    const isMe = player.id === myId;
    const isActionCompleted = player.actionCompleted;
    // PLAYER_TURN 단계에서 액션이 완료되지 않은 모든 플레이어 (동시 진행)
    const shouldShowThinking =
      gamePhase === BlackjackGamePhase.PLAYER_TURN && !isActionCompleted;

    // 디버깅용 로그 (임시)
    if (!isActionCompleted && gamePhase === BlackjackGamePhase.PLAYER_TURN) {
      console.log(`Player ${player.name}:`, {
        isActionCompleted,
        gamePhase,
        shouldShowThinking,
        playerState: player,
      });
    }

    return (
      <div
        key={player.id}
        className={`
          bg-gradient-to-r ${
            player.isEliminated
              ? "from-gray-900/80 to-gray-800/80"
              : isMe
              ? "from-blue-900/80 to-blue-800/80"
              : "from-slate-800/80 to-slate-700/80"
          } backdrop-blur-sm rounded-xl px-3 py-2 border ${
          player.isEliminated
            ? "border-red-600/30"
            : isMe
            ? "border-blue-600/30"
            : "border-slate-600/30"
        } shadow-lg transition-opacity duration-300 h-36 lg:h-32 ${
          player.isEliminated ? "opacity-60 grayscale" : ""
        }
        `}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center min-w-0 flex-1">
            <span className="text-white font-medium text-sm truncate">
              {player.name}
            </span>

            {/* 액션 상태 표시 */}
            {!player.isEliminated &&
              (shouldShowThinking ? (
                <span className="ml-2 text-lg animate-bounce">🤔</span>
              ) : isActionCompleted ? (
                <span className="ml-2 text-green-400 font-bold text-xs">✓</span>
              ) : null)}
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-gray-300 text-xs">
              {t("Dealerjack.availableChips")}
            </div>
            <div className="flex items-center text-yellow-400 font-bold text-sm">
              <i className="fas fa-coins mr-1 text-xs"></i>
              <span>{player.chips}</span>
            </div>
          </div>
        </div>

        {/* 베팅 정보 */}
        {showBets && player.currentBet > 0 && (
          <div className="flex items-center text-orange-400 text-xs mb-1">
            <i className="fas fa-coins mr-1"></i>
            <span className="font-bold">{player.currentBet}</span>
            {player.isAllIn && !player.isEliminated && (
              <span className="ml-1 text-orange-400 font-bold">All in</span>
            )}
            {player.isDoubledDown && (
              <span className="ml-1 text-purple-400 font-bold">(2X)</span>
            )}
          </div>
        )}

        {/* 카드 및 핸드 값 */}
        {renderPlayerCards(player)}
      </div>
    );
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {/* 플레이어 목록 - 반응형 그리드 (2/3/4열) */}
      {players.map((player) => renderPlayerCard(player))}
    </div>
  );
};

export default DealerJackPlayerList;
